import { useState } from 'react';
import type { Comment } from '../../api/client';
import type { ResolvedMarkerPosition } from '../../hooks/useResolvedCommentAnchors';

const MARKER_COLORS = ['#16894A', '#2563EB', '#B47A00', '#6B5BD4', '#C4305A', '#0F766E', '#B45309'];

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function markerColor(comment: Comment): string {
  return MARKER_COLORS[hashString(comment.author.id || comment.author.name) % MARKER_COLORS.length];
}

function markerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || '?').toUpperCase();
}

interface CommentMarkersProps {
  comments: Comment[];
  onMarkerClick: (commentId: string) => void;
  onMarkerHover?: (comment: Comment) => void;
  onMarkerLeave?: () => void;
  resolvedPositions: Record<string, ResolvedMarkerPosition>;
  hasResolverResponded: boolean;
  iframeScrollY?: number;
  iframeViewportHeight?: number;
  highlightedCommentId?: string | null;
}

export default function CommentMarkers({
  comments,
  onMarkerClick,
  onMarkerHover,
  onMarkerLeave,
  resolvedPositions,
  hasResolverResponded,
  iframeScrollY = 0,
  iframeViewportHeight = 1,
  highlightedCommentId,
}: CommentMarkersProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const markable = comments.filter(
    (comment) =>
      !comment.parent_id &&
      !comment.resolved_at &&
      comment.anchor &&
      comment.anchor.type !== 'page' &&
      (resolvedPositions[comment.id] ||
        (!hasResolverResponded && comment.anchor.type === 'text' && comment.anchor.rect)),
  );

  if (markable.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-40">
      {markable.map((comment) => {
        const { anchor } = comment;
        if (!anchor || anchor.type === 'page') return null;

        let pos = resolvedPositions[comment.id];
        if (!pos && !hasResolverResponded && anchor.type === 'text' && anchor.rect) {
          const creationScrollY = anchor.scrollY ?? 0;
          const docYPx = (anchor.rect.y / 100) * iframeViewportHeight + creationScrollY;
          const viewportYPx = docYPx - iframeScrollY;
          pos = {
            x: anchor.rect.x + anchor.rect.width,
            y: (viewportYPx / iframeViewportHeight) * 100,
          };
        }
        if (!pos || pos.y < -5 || pos.y > 105) return null;

        const isHighlighted = highlightedCommentId === comment.id;
        const isActive = hoveredId === comment.id || isHighlighted;
        const markerLabel = markerInitials(comment.author.name);
        const color = markerColor(comment);

        return (
          <div
            key={comment.id}
            className="absolute"
            style={{
              left: `${Math.min(Math.max(pos.x, 2), 98)}%`,
              top: `${Math.min(Math.max(pos.y, 2), 98)}%`,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'auto',
              zIndex: isActive ? 2 : 1,
            }}
          >
            <button
              type="button"
              onClick={() => onMarkerClick(comment.id)}
              onMouseEnter={() => {
                setHoveredId(comment.id);
                onMarkerHover?.(comment);
              }}
              onMouseLeave={() => {
                setHoveredId(null);
                onMarkerLeave?.();
              }}
              className="relative flex h-[24px] w-[24px] items-center justify-center p-0 text-[10px] font-bold"
              style={{
                background: color,
                border: '2px solid #ffffff',
                borderRadius: '50% 50% 50% 4px',
                color: '#ffffff',
                boxShadow: isActive
                  ? `0 0 0 3px ${color}33, 0 4px 10px rgba(0,0,0,.14)`
                  : '0 1px 4px rgba(0,0,0,.12), 0 0 0 0.5px rgba(0,0,0,0.05)',
                cursor: 'pointer',
                transform: `rotate(-45deg) ${isActive ? 'scale(1.10)' : 'scale(1)'}`,
                transition: 'transform 160ms cubic-bezier(.2,.8,.2,1), box-shadow 160ms',
              }}
              aria-label={`Comment by ${comment.author.name}`}
              title={`${comment.author.name}: ${comment.body.slice(0, 60)}`}
            >
              <span style={{ transform: 'rotate(45deg)', letterSpacing: '0.02em' }}>{markerLabel}</span>
            </button>

            {hoveredId === comment.id && (
              <div
                className="absolute left-1/2 top-[30px] w-[220px] rounded-[7px] px-[10px] py-[8px]"
                style={{
                  transform: 'translateX(-50%)',
                  background: 'var(--rd-surface-panel)',
                  border: '1px solid var(--rd-border)',
                  boxShadow: '0 6px 18px rgba(20,28,40,0.08), 0 1px 3px rgba(20,28,40,0.04)',
                  pointerEvents: 'none',
                }}
              >
                <p className="text-[12px] font-semibold m-0 mb-[2px] truncate" style={{ color: 'var(--rd-text-strong)' }}>
                  {comment.author.name}
                </p>
                <p
                  className="text-[12px] m-0 line-clamp-2"
                  style={{
                    color: 'var(--rd-text-medium)',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {comment.body}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
